import { type ComponentType, createContext, useContext } from 'react';

/**
 * The compose opener (F20-c spec FR-O1) — the one way any feature opens the compose dialog.
 *
 * The types, the context and `useComposeEmail()` live here, in `src/lib`, so a panel in any feature
 * folder opens the dialog without importing `src/features/email` (`features-do-not-import-each-other`
 * and `lib-is-a-leaf` in `.dependency-cruiser.cjs`). `src/app/ComposeEmailProvider.tsx` fills the
 * context from the email feature's dialog and is mounted once, in `AppLayout`. The pattern is
 * `src/lib/session/sessionContext.ts` (types and context here, the provider elsewhere).
 *
 * The names below are fixed by F20-c WI-1.2's plan (Approach, "What this WI locks for WI-1.3"); F11-h,
 * F20-d, F26-d, F6-c and F1-c open the dialog through them.
 */

/** The wire's `kind` values (F20-c Wave 1 contract, `SendEmailRequest.kind`). */
export type ComposeEmailKind = 'NEW' | 'REPLY' | 'REPLY_ALL' | 'FORWARD';

/** The wire's `SendOrigin.type` values. */
export type ComposeEmailOriginType = 'LEAD' | 'ACCOUNT' | 'CONTACT';

/** The record a send is made from — the wire's `SendOrigin`. */
export interface ComposeEmailOrigin {
    readonly type: ComposeEmailOriginType;
    readonly id: number;
}

/**
 * The shared `aiProvenance` record (F12 FR-B8; `forge-et-be` `docs/api/payloads.md` § AI provenance),
 * declared structurally because `src/lib` may not reach a feature. `confidence` is the model's own
 * 0–100 estimate; `source.ref` is an API path, or `null` when the source has no record.
 */
export interface ComposeEmailAiProvenance {
    readonly confidence: number;
    readonly source: { readonly label: string; readonly ref: string | null };
    readonly edited: boolean;
    readonly editedAt: string | null;
}

/**
 * What the dialog opens with (F20-c FR-O1). The dialog shows each member exactly as given. `sourceMessageId`
 * is set for `REPLY`, `REPLY_ALL` and `FORWARD` only; `bodyHtml` is HTML — a caller holding plain text
 * converts it with `plainTextToHtml` (`./plainTextToHtml.ts`).
 */
export interface ComposeEmailPrefill {
    readonly kind: ComposeEmailKind;
    readonly sourceMessageId?: number;
    readonly origin?: ComposeEmailOrigin;
    readonly to?: readonly string[];
    readonly cc?: readonly string[];
    readonly bcc?: readonly string[];
    readonly subject?: string;
    readonly bodyHtml?: string;
    readonly aiProvenance?: ComposeEmailAiProvenance;
}

/** One request to open the dialog: the pre-fill and the two callbacks. */
export interface ComposeEmailRequest {
    readonly prefill: ComposeEmailPrefill;
    /** Called with the send's id (`SendEmailResponse.id`) after a `201`. */
    readonly onSent?: (sendId: number) => void;
    /** Called when the dialog closes without sending. */
    readonly onClosed?: () => void;
    /**
     * The caller shows the send's success message itself; the layer raises no *Email sent to …* toast
     * (F11-h WI-1.2 plan Decision 20; an F20-c spec revision). Absent or `false`: the layer's toast, as
     * for every other caller. `onSent` is called either way.
     */
    readonly ownsSentMessage?: boolean;
}

/**
 * Opens the dialog. A call while a dialog is already open is ignored — one dialog at a time; a call
 * made while one is closing runs once it has closed (BUG-134).
 */
export type OpenComposeEmail = (request: ComposeEmailRequest) => void;

/**
 * The context `ComposeEmailProvider` fills. `null` outside the provider, which `useComposeEmail`
 * reports. Exported for tests that mount a stand-in opener.
 */
export const ComposeEmailContext = createContext<OpenComposeEmail | null>(null);

/** The message `useComposeEmail` throws with outside the provider. */
export const COMPOSE_EMAIL_OUTSIDE_PROVIDER = 'useComposeEmail must be used inside ComposeEmailProvider';

/** The opener the provider supplies. Throws a named error outside `ComposeEmailProvider`. */
export function useComposeEmail(): OpenComposeEmail {
    const open = useContext(ComposeEmailContext);
    if (open === null) {
        throw new Error(COMPOSE_EMAIL_OUTSIDE_PROVIDER);
    }
    return open;
}

/** What an `aiDraft` filler hands the dialog: the subject, the body (HTML) and the provenance. */
export interface ComposeAiDraft {
    readonly subject: string;
    readonly bodyHtml: string;
    readonly aiProvenance: ComposeEmailAiProvenance;
}

/**
 * What the component in the dialog's `aiDraft` place receives (F20-c FR-C8): the dialog's kind, origin and
 * source message, the callback that fills the subject and body with a draft, whether a send is in
 * flight (the place should not apply a draft then), and whether the person has written text a draft
 * would replace.
 */
export interface ComposeAiDraftPlaceProps {
    readonly kind: ComposeEmailKind;
    readonly origin: ComposeEmailOrigin | null;
    readonly sourceMessageId: number | null;
    readonly applyDraft: (draft: ComposeAiDraft) => void;
    readonly busy: boolean;
    /**
     * True when the person has typed a subject or body in the dialog (F20-c spec `## Revisions`, F11-h
     * spec FR-V4): a change they made to the subject or the body, or a template they inserted, with text
     * still there. A pre-filled subject or body and an applied AI draft are not the person's text.
     */
    readonly hasUserText: boolean;
}

/**
 * The composition `src/app/composeEmailComposition.ts` hands the dialog: the `aiDraft` filler (F11-h's
 * *AI draft* button; empty in F20-c) and where a place's render error is reported.
 */
export interface ComposeEmailComposition {
    readonly aiDraft?: ComponentType<ComposeAiDraftPlaceProps>;
    readonly reportPlaceError?: (error: unknown) => void;
}
